import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BedDouble, LogIn, LogOut, Inbox, HandHelping, Users } from "lucide-react";
import { BRAND, errMsg } from "./constants";

export function OverviewPanel({ pid, authFetch, API }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/overview` }).then((r) => setD(r.data)).catch(() => {}), [pid, authFetch, API]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (!d) return <div className="text-sm" style={{ color: BRAND.muted }}>Loading…</div>;
  const tiles = [
    ["ov-occupied", Users, "Occupied", `${d.occupied}/${d.total_beds}`, BRAND.green, false],
    ["ov-empty", BedDouble, "Empty beds", d.empty_beds, "#FF8C1A", d.empty_beds > 0],
    ["ov-checkins", LogIn, "Check-ins today", d.checkins_today, BRAND.green, false],
    ["ov-checkouts", LogOut, "Check-outs today", d.checkouts_today, BRAND.orange, false],
    ["ov-requests", Inbox, "Pending requests", d.pending_requests, "#DC2626", d.pending_requests > 0],
    ["ov-assist", HandHelping, "Assist flags", d.assist_flags, "#2563EB", d.assist_flags > 0],
  ];
  return (
    <div data-testid="overview-panel" className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {tiles.map(([tid, Icon, l, v, c, blink], i) => (
        <div key={tid} data-testid={tid} className="rounded-2xl border-2 bg-white p-4 hs-rise" style={{ borderColor: BRAND.line, animationDelay: `${i * 60}ms` }}>
          <div className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5" style={{ color: BRAND.muted }}><Icon className="w-3.5 h-3.5" style={{ color: c }} /> {l}</div>
          <div className={`mt-1 font-display text-3xl font-bold ${blink ? "bed-blink" : ""}`} style={{ color: c }} data-testid={`${tid}-value`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

export function ExtendAssistPanel({ pid, authFetch, API }) {
  const [ext, setExt] = useState([]);
  const [flags, setFlags] = useState([]);
  const load = useCallback(() => {
    authFetch({ url: `${API}/homestay/properties/${pid}/extend-requests` }).then((r) => setExt(r.data.items)).catch(() => {});
    authFetch({ url: `${API}/homestay/properties/${pid}/assist-flags` }).then((r) => setFlags(r.data.items)).catch(() => {});
  }, [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);
  const decide = async (x, action) => {
    let data = { action };
    if (action === "counter") {
      const co = window.prompt("Counter check-out date (YYYY-MM-DD)", x.new_check_out);
      if (!co) return;
      const amt = window.prompt("Extra amount ₹ for the extension", "0");
      data = { ...data, counter_check_out: co, counter_amount_inr: +amt || 0 };
    }
    try { await authFetch({ method: "post", url: `${API}/homestay/extend/${x.id}/decide`, data }); toast.success(`Request ${action === "counter" ? "countered" : action + "d"}`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not update")); }
  };
  const done = async (f) => {
    try { await authFetch({ method: "post", url: `${API}/homestay/assist-flags/${f.id}/done` }); load(); } catch (e) { toast.error(errMsg(e, "Failed")); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div data-testid="extend-panel" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
        <div className="text-sm font-bold" style={{ color: BRAND.green }}>Extend stay requests</div>
        {ext.length === 0 && <div className="text-xs mt-2" style={{ color: BRAND.muted }}>No extend requests.</div>}
        {ext.map((x) => (
          <div key={x.id} data-testid={`extend-row-${x.id}`} className="py-2.5 border-t text-sm" style={{ borderColor: BRAND.line }}>
            <div className="flex justify-between gap-2"><b>{x.guest_name}</b><span data-testid={`extend-status-${x.id}`} className="text-[10px] font-bold uppercase" style={{ color: x.status === "pending" ? BRAND.orange : BRAND.green }}>{x.status}</span></div>
            <div className="text-xs" style={{ color: BRAND.muted }}>{x.current_check_out?.slice(0, 10) || "open"} → <b>{x.new_check_out}</b>{x.message ? ` · "${x.message}"` : ""}{x.counter_check_out ? ` · counter ${x.counter_check_out} (+₹${x.counter_amount_inr})` : ""}</div>
            {x.status === "pending" && (
              <div className="mt-1.5 flex gap-2">
                {[["approve", BRAND.green], ["counter", BRAND.orange], ["decline", "#DC2626"]].map(([a, c]) => (
                  <button key={a} data-testid={`extend-${a}-${x.id}`} onClick={() => decide(x, a)} className="rounded-full border px-2.5 py-1 text-[11px] font-bold capitalize" style={{ borderColor: c, color: c }}>{a === "counter" ? "Counter-offer" : a}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div data-testid="assist-flags-panel" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
        <div className="text-sm font-bold" style={{ color: BRAND.green }}>Assist flags from guests</div>
        {flags.length === 0 && <div className="text-xs mt-2" style={{ color: BRAND.muted }}>No assist requests.</div>}
        {flags.map((f) => (
          <div key={f.id} data-testid={`assist-flag-${f.id}`} className="py-2 border-t text-sm flex justify-between gap-2" style={{ borderColor: BRAND.line }}>
            <span><b>{f.guest_name}</b> · {f.label}<span className="block text-xs" style={{ color: BRAND.muted }}>{f.guest_phone}{f.note ? ` · ${f.note}` : ""}</span></span>
            {f.status === "open" ? <button data-testid={`assist-done-${f.id}`} onClick={() => done(f)} className="rounded-full border px-2.5 py-1 text-[11px] font-bold shrink-0" style={{ borderColor: BRAND.green, color: BRAND.green }}>Mark handled</button>
              : <span className="text-[10px] font-bold uppercase" style={{ color: BRAND.green }}>handled</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
